// src/features/game/pages/EditTemplatePage.tsx

import React, {
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  ArrowLeft,
} from 'lucide-react';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import RouteConfig from '../../../routes/RouteConfig';

import {
  deleteBlind,
  deleteReEntry,
  fetchGameTemplates,
  updateBlind,
  updateReEntry,
  updateTemplateBasic,
} from '../services/gameService';

import type {
  Blind,
  CreateTemplateRequest,
  GameTemplate,
  ReEntry,
  UpdateBlindRequest,
  UpdateReEntryRequest,
  UpdateTemplateBasicRequest,
} from '../types/game.types';

import TemplateFormFields from '../components/TemplateFormFields';
import BlindListEditor from '../components/BlindListEditor';
import ReEntryListEditor from '../components/ReEntryListEditor';

type TemplateFormData = Omit<
  CreateTemplateRequest,
  'blinds' | 'reEntries'
>;

type TemplateFormErrors =
  Partial<
    Record<
      keyof TemplateFormData,
      string
    >
  >;

const INITIAL_FORM_DATA: TemplateFormData = {
  gameName: '',
  gameStack: 0,
  gamePrice: 0,
  gameLimit: 0,
  buyinEndLevel: 0,
  gameEndLevel: 0,
  earlyAmount: 0,
};

const EditTemplatePage:
  React.FC = () => {
  const navigate =
    useNavigate();

  const modal =
    useModal();

  const {
    id,
  } =
    useParams<{
      id: string;
    }>();

  const gameId =
    Number(id);

  const [
    initialTemplate,
    setInitialTemplate,
  ] =
    useState<GameTemplate | null>(
      null,
    );

  const [
    formData,
    setFormData,
  ] =
    useState<TemplateFormData>(
      INITIAL_FORM_DATA,
    );

  const [
    blinds,
    setBlinds,
  ] =
    useState<Blind[]>(
      [],
    );

  const [
    reEntries,
    setReEntries,
  ] =
    useState<ReEntry[]>(
      [],
    );

  const [
    loading,
    setLoading,
  ] =
    useState<boolean>(
      true,
    );

  const [
    saving,
    setSaving,
  ] =
    useState<boolean>(
      false,
    );

  const [
    errors,
    setErrors,
  ] =
    useState<TemplateFormErrors>(
      {},
    );

  /**
   * CreateGamePage로 이동
   */
  const navigateToCreateGame =
    (): void => {
      navigate(
        RouteConfig.Management
          .GameCreate.fullPath,
      );
    };

  /**
   * 템플릿 조회
   */
  useEffect(() => {
    let isActive =
      true;

    const loadTemplate =
      async (): Promise<void> => {
        if (
          !id ||
          Number.isNaN(
            gameId,
          )
        ) {
          await modal.alert(
            '잘못된 템플릿 번호입니다.',
          );

          navigateToCreateGame();

          return;
        }

        try {
          setLoading(
            true,
          );

          const templates =
            await fetchGameTemplates();

          if (!isActive) {
            return;
          }

          const data =
            templates.find(
              (
                template:
                  GameTemplate,
              ) =>
                template.gameId ===
                gameId,
            );

          if (!data) {
            await modal.alert(
              '템플릿을 찾을 수 없습니다.',
            );

            navigateToCreateGame();

            return;
          }

          setInitialTemplate(
            data,
          );

          setFormData({
            gameName:
              data.gameName,

            gameStack:
              data.gameStack,

            gamePrice:
              data.gamePrice,

            gameLimit:
              data.gameLimit,

            buyinEndLevel:
              data.buyinEndLevel,

            gameEndLevel:
              data.gameEndLevel,

            earlyAmount:
              data.earlyAmount ??
              0,
          });

          setBlinds(
            data.blinds.map(
              (
                blind:
                  Blind,
              ) => ({
                ...blind,
              }),
            ),
          );

          setReEntries(
            data.reEntries.map(
              (
                reEntry:
                  ReEntry,
              ) => ({
                ...reEntry,
              }),
            ),
          );
        } catch (
          error: unknown
        ) {
          console.error(
            '템플릿 조회 실패:',
            error,
          );

          if (
            isActive
          ) {
            await modal.alert(
              '템플릿을 불러오는데 실패했습니다.',
            );

            navigateToCreateGame();
          }
        } finally {
          if (
            isActive
          ) {
            setLoading(
              false,
            );
          }
        }
      };

    void loadTemplate();

    return () => {
      isActive = false;
    };
  }, [
    id,
    gameId,
    modal,
  ]);

  /**
   * 기본정보 변경
   */
  const handleFieldChange = (
    field:
      keyof TemplateFormData,
    value:
      string | number,
  ): void => {
    setFormData(
      (
        previous,
      ) => ({
        ...previous,

        [field]:
          value,
      }),
    );

    setErrors(
      (
        previous,
      ) => {
        if (
          !previous[field]
        ) {
          return previous;
        }

        return {
          ...previous,

          [field]:
            undefined,
        };
      },
    );
  };

  /**
   * Validation
   */
  const validate =
    async (): Promise<boolean> => {
      const newErrors:
        TemplateFormErrors = {};

      if (
        !formData.gameName.trim()
      ) {
        newErrors.gameName =
          '게임명을 입력해주세요.';
      }

      if (
        formData.gameStack <=
        0
      ) {
        newErrors.gameStack =
          '시작 스택은 0보다 커야 합니다.';
      }

      if (
        formData.gamePrice <=
        0
      ) {
        newErrors.gamePrice =
          '바이인 금액은 0보다 커야 합니다.';
      }

      if (
        formData.gameLimit <=
        0
      ) {
        newErrors.gameLimit =
          '바이인 제한은 1회 이상이어야 합니다.';
      }

      if (
        formData.buyinEndLevel <=
        0
      ) {
        newErrors.buyinEndLevel =
          '바이인 종료 레벨은 1 이상이어야 합니다.';
      }

      if (
        formData.gameEndLevel <=
        0
      ) {
        newErrors.gameEndLevel =
          '게임 종료 레벨은 1 이상이어야 합니다.';
      }

      if (
        formData.buyinEndLevel >
        formData.gameEndLevel
      ) {
        newErrors.buyinEndLevel =
          '바이인 종료 레벨은 게임 종료 레벨보다 작거나 같아야 합니다.';
      }

      if (
        formData.earlyAmount <
        0
      ) {
        newErrors.earlyAmount =
          '얼리칩은 0 이상이어야 합니다.';
      }

      setErrors(
        newErrors,
      );

      if (
        Object.keys(
          newErrors,
        ).length > 0
      ) {
        return false;
      }

      if (
        blinds.length ===
        0
      ) {
        await modal.alert(
          '블라인드를 최소 1개 이상 추가해주세요.',
        );

        return false;
      }

      const blindLevels =
        blinds.map(
          (
            blind:
              Blind,
          ) =>
            blind.level,
        );

      if (
        new Set(
          blindLevels,
        ).size !==
        blindLevels.length
      ) {
        await modal.alert(
          '중복된 블라인드 레벨이 있습니다.',
        );

        return false;
      }

      for (
        const blind of
        blinds
      ) {
        const isBreak =
          blind.isBreak ===
            true ||
          (
            blind.smallBlind ===
              null &&
            blind.bigBlind ===
              null
          );

        if (
          blind.level <=
          0
        ) {
          await modal.alert(
            '블라인드 레벨은 1 이상이어야 합니다.',
          );

          return false;
        }

        if (
          blind.duration <=
          0
        ) {
          await modal.alert(
            `Level ${blind.level}: 시간은 1분 이상이어야 합니다.`,
          );

          return false;
        }

        if (isBreak) {
          continue;
        }

        if (
          blind.smallBlind ===
            null ||
          blind.bigBlind ===
            null
        ) {
          await modal.alert(
            `Level ${blind.level}: 스몰 블라인드와 빅 블라인드를 입력해주세요.`,
          );

          return false;
        }

        if (
          blind.bigBlind <=
          blind.smallBlind
        ) {
          await modal.alert(
            `Level ${blind.level}: 빅 블라인드는 스몰 블라인드보다 커야 합니다.`,
          );

          return false;
        }

        if (
          blind.ante !==
            null &&
          blind.ante < 0
        ) {
          await modal.alert(
            `Level ${blind.level}: 앤티는 0 이상이어야 합니다.`,
          );

          return false;
        }
      }

      const reEntryCounts =
        reEntries.map(
          (
            reEntry:
              ReEntry,
          ) =>
            reEntry.count,
        );

      if (
        new Set(
          reEntryCounts,
        ).size !==
        reEntryCounts.length
      ) {
        await modal.alert(
          '중복된 리엔트리 횟수가 있습니다.',
        );

        return false;
      }

      for (
        const reEntry of
        reEntries
      ) {
        if (
          reEntry.count <=
          0
        ) {
          await modal.alert(
            '리엔트리 횟수는 1 이상이어야 합니다.',
          );

          return false;
        }

        if (
          reEntry.price <=
          0
        ) {
          await modal.alert(
            `${reEntry.count}회 리엔트리 금액은 0보다 커야 합니다.`,
          );

          return false;
        }

        if (
          reEntry.stack <=
          0
        ) {
          await modal.alert(
            `${reEntry.count}회 리엔트리 스택은 0보다 커야 합니다.`,
          );

          return false;
        }
      }

      return true;
    };

  const hasBasicInfoChanged =
    (): boolean => {
      if (
        !initialTemplate
      ) {
        return false;
      }

      return (
        formData.gameName.trim() !==
          initialTemplate.gameName ||
        formData.gameStack !==
          initialTemplate.gameStack ||
        formData.gamePrice !==
          initialTemplate.gamePrice ||
        formData.gameLimit !==
          initialTemplate.gameLimit ||
        formData.buyinEndLevel !==
          initialTemplate.buyinEndLevel ||
        formData.gameEndLevel !==
          initialTemplate.gameEndLevel ||
        formData.earlyAmount !==
          (
            initialTemplate.earlyAmount ??
            0
          )
      );
    };

  const hasBlindChanged = (
    blind: Blind,
    initial:
      Blind | undefined,
  ): boolean => {
    if (!initial) {
      return true;
    }

    return (
      blind.level !==
        initial.level ||
      blind.smallBlind !==
        initial.smallBlind ||
      blind.bigBlind !==
        initial.bigBlind ||
      blind.ante !==
        initial.ante ||
      blind.duration !==
        initial.duration ||
      Boolean(
        blind.isBreak,
      ) !==
        Boolean(
          initial.isBreak,
        )
    );
  };

  const hasReEntryChanged = (
    reEntry:
      ReEntry,
    initial:
      ReEntry | undefined,
  ): boolean => {
    if (!initial) {
      return true;
    }

    return (
      reEntry.price !==
        initial.price ||
      reEntry.stack !==
        initial.stack
    );
  };

  const getDeletedBlinds =
    (): Blind[] => {
      if (
        !initialTemplate
      ) {
        return [];
      }

      return initialTemplate.blinds.filter(
        (
          initial:
            Blind,
        ) =>
          !blinds.some(
            (
              current:
                Blind,
            ) =>
              current.level ===
              initial.level,
          ),
      );
    };

  const getDeletedReEntries =
    (): ReEntry[] => {
      if (
        !initialTemplate
      ) {
        return [];
      }

      return initialTemplate.reEntries.filter(
        (
          initial:
            ReEntry,
        ) =>
          !reEntries.some(
            (
              current:
                ReEntry,
            ) =>
              current.count ===
              initial.count,
          ),
      );
    };

  /**
   * 저장
   */
  const handleSave =
    async (): Promise<void> => {
      const isValid =
        await validate();

      if (
        !isValid ||
        !initialTemplate
      ) {
        return;
      }

      const confirmed =
        await modal.confirm(
          '변경사항을 저장하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setSaving(
          true,
        );

        const deletePromises:
          Promise<void>[] = [];

        const deletedBlinds =
          getDeletedBlinds();

        deletedBlinds.forEach(
          (
            blind:
              Blind,
          ) => {
            deletePromises.push(
              deleteBlind(
                gameId,
                blind.level,
              ),
            );
          },
        );

        const deletedReEntries =
          getDeletedReEntries();

        deletedReEntries.forEach(
          (
            reEntry:
              ReEntry,
          ) => {
            deletePromises.push(
              deleteReEntry(
                gameId,
                reEntry.count,
              ),
            );
          },
        );

        const updatePromises:
          Promise<unknown>[] = [];

        if (
          hasBasicInfoChanged()
        ) {
          const basicRequest:
            UpdateTemplateBasicRequest = {
            gameName:
              formData.gameName.trim(),

            gameStack:
              formData.gameStack,

            gamePrice:
              formData.gamePrice,

            gameLimit:
              formData.gameLimit,

            buyinEndLevel:
              formData.buyinEndLevel,

            gameEndLevel:
              formData.gameEndLevel,

            earlyAmount:
              formData.earlyAmount,
          };

          updatePromises.push(
            updateTemplateBasic(
              gameId,
              basicRequest,
            ),
          );
        }

        blinds.forEach(
          (
            blind:
              Blind,
          ) => {
            const initial =
              initialTemplate.blinds.find(
                (
                  item:
                    Blind,
                ) =>
                  item.level ===
                  blind.level,
              );

            if (
              hasBlindChanged(
                blind,
                initial,
              )
            ) {
              const request:
                UpdateBlindRequest = {
                level:
                  blind.level,

                smallBlind:
                  blind.smallBlind,

                bigBlind:
                  blind.bigBlind,

                ante:
                  blind.ante,

                duration:
                  blind.duration,

                isBreak:
                  blind.isBreak,
              };

              updatePromises.push(
                updateBlind(
                  gameId,
                  blind.level,
                  request,
                ),
              );
            }
          },
        );

        reEntries.forEach(
          (
            reEntry:
              ReEntry,
          ) => {
            const initial =
              initialTemplate.reEntries.find(
                (
                  item:
                    ReEntry,
                ) =>
                  item.count ===
                  reEntry.count,
              );

            if (
              hasReEntryChanged(
                reEntry,
                initial,
              )
            ) {
              const request:
                UpdateReEntryRequest = {
                price:
                  reEntry.price,

                stack:
                  reEntry.stack,
              };

              updatePromises.push(
                updateReEntry(
                  gameId,
                  reEntry.count,
                  request,
                ),
              );
            }
          },
        );

        if (
          deletePromises.length ===
            0 &&
          updatePromises.length ===
            0
        ) {
          await modal.alert(
            '변경사항이 없습니다.',
          );

          return;
        }

        if (
          deletePromises.length >
          0
        ) {
          await Promise.all(
            deletePromises,
          );
        }

        if (
          updatePromises.length >
          0
        ) {
          await Promise.all(
            updatePromises,
          );
        }

        await modal.alert(
          '템플릿이 수정되었습니다.',
        );

        /**
         * 저장 후 CreateGamePage로 복귀
         */
        navigateToCreateGame();
      } catch (
        error: unknown
      ) {
        console.error(
          '템플릿 수정 실패:',
          error,
        );

        await modal.alert(
          '템플릿 수정에 실패했습니다.',
        );
      } finally {
        setSaving(
          false,
        );
      }
    };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-20-regular text-gray-600">
          로딩 중...
        </div>
      </div>
    );
  }

  if (
    !initialTemplate
  ) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={
              navigateToCreateGame
            }
            disabled={
              saving
            }
            className="grid h-10 w-10 place-items-center rounded-lg text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
            aria-label="게임 생성 페이지로 돌아가기"
          >
            <ArrowLeft
              size={24}
            />
          </button>

          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              템플릿 수정
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              게임 기본 정보와 블라인드,
              리엔트리 구조를 수정합니다.
            </p>
          </div>
        </header>

        <div className="space-y-6">
          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              1. 기본 정보
            </h2>

            <TemplateFormFields
              formData={
                formData
              }
              onChange={
                handleFieldChange
              }
              errors={
                errors
              }
            />
          </section>

          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              2. 블라인드 구조
            </h2>

            <BlindListEditor
              blinds={
                blinds
              }
              onChange={
                setBlinds
              }
            />
          </section>

          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
              3. 리엔트리 옵션
            </h2>

            <ReEntryListEditor
              reEntries={
                reEntries
              }
              onChange={
                setReEntries
              }
            />
          </section>

          <footer className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={
                navigateToCreateGame
              }
              disabled={
                saving
              }
              className="rounded-lg bg-gray-200 px-6 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              취소
            </button>

            <button
              type="button"
              onClick={() =>
                void handleSave()
              }
              disabled={
                saving
              }
              className="rounded-lg bg-main px-6 py-3 text-sm font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              {saving
                ? '저장 중...'
                : '저장'}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default EditTemplatePage;